/**
 * DateField — site-wide progressive enhancement for <input type="date">.
 *
 * Every native date input (YYYY-MM-DD — the canonical value the app reads, submits, and stores) is
 * paired with a visible, fillable MM/DD/YYYY text twin; the native input becomes an invisible
 * calendar-button overlay. Typing into the twin updates the native (empty while incomplete/invalid);
 * picking from the calendar reflects back into the twin. This is the ONE mechanism used across every
 * page (intake, results, portal, enroll) and for dynamically-rendered rows (child DOB fields).
 *
 * upgradeDateInputs(root) is idempotent (guarded by data-date-wired) so it is safe to call on init
 * AND after each dynamic render. It also wires fields already wrapped in static .date-field markup.
 *
 * Skips inputs inside .quote-state-store (hidden hydration holders) and [data-no-date-upgrade].
 */
import { isoToMDY, mdyToISO, maskDateInput } from './format.js';

var _seq = 0;

function isDobLike(input) {
  var key = (input.id || input.name || '').toLowerCase();
  return /dob|birth|bday/.test(key) || input.getAttribute('autocomplete') === 'bday';
}

// Human label for the calendar button's aria-label: the field's <label>, its aria-label, else "date".
function labelTextFor(input) {
  var lbl = input.id ? document.querySelector('label[for="' + input.id + '"]') : null;
  if (!lbl && input.closest) {                 // name-only fields (e.g. enroll child rows): no for=
    var group = input.closest('.form-group');
    if (group) lbl = group.querySelector('label');
  }
  var text = (lbl && lbl.textContent) || input.getAttribute('aria-label') || 'date';
  return text.replace(/\s*\*\s*$/, '').replace(/\s+/g, ' ').trim() || 'date';
}

// Build the .date-field wrapper around a bare native date input; returns the new text twin.
function buildWrapper(input) {
  var label = labelTextFor(input);

  var twin = document.createElement('input');
  twin.type = 'text';
  twin.className = 'date-field__text';
  twin.placeholder = 'MM/DD/YYYY';
  twin.setAttribute('inputmode', 'numeric');
  twin.maxLength = 10;
  if (isDobLike(input)) twin.setAttribute('autocomplete', 'bday');
  var twinId = input.id ? input.id + 'Text' : 'dateText_' + (++_seq);
  twin.id = twinId;

  // The visible twin carries `required` + the label association; the hidden native must not (an
  // opacity:0 required control throws "An invalid form control is not focusable" on native submit).
  if (input.required) { twin.required = true; input.required = false; }
  var hasForLabel = false;
  if (input.id) {
    var forLbl = document.querySelector('label[for="' + input.id + '"]');
    if (forLbl) { forLbl.setAttribute('for', twinId); hasForLabel = true; }
  }
  if (!hasForLabel) twin.setAttribute('aria-label', label);   // accessible name for name-only fields

  var field = document.createElement('div');
  field.className = 'date-field';
  var picker = document.createElement('span');
  picker.className = 'date-field__picker';
  var icon = document.createElement('span');
  icon.className = 'date-field__icon';
  icon.setAttribute('aria-hidden', 'true');
  icon.textContent = '📅';

  input.parentNode.insertBefore(field, input);   // drop the wrapper where the input was
  input.classList.add('date-field__native');
  input.setAttribute('tabindex', '-1');
  input.setAttribute('aria-label', 'Pick ' + label + ' from calendar');

  picker.appendChild(icon);
  picker.appendChild(input);                      // moves the native out of its old slot into the picker
  field.appendChild(twin);
  field.appendChild(picker);
  return twin;
}

/** Upgrade + wire a single native date input. Idempotent. Returns its text twin (or null if skipped). */
export function upgradeDateInput(input) {
  if (!input || input.dataset.dateWired === '1') return null;
  if (input.closest('.quote-state-store') || input.closest('[data-no-date-upgrade]')) return null;

  var wrapper = input.closest('.date-field');
  var twin = wrapper
    ? ((input.id && document.getElementById(input.id + 'Text')) || wrapper.querySelector('.date-field__text'))
    : buildWrapper(input);
  if (!twin) return null;

  // Mirror the native's validity onto the (visible, required) twin so a native <form> submit shows the
  // bubble on the VISIBLE field: a required twin is satisfied by ANY text, but an unparseable/partial
  // date leaves the native empty, and a native min/max (e.g. effective date >= today) only validates
  // the native — including when the date is chosen from the calendar.
  function refreshTwinValidity() {
    var msg = '';
    if (twin.value.trim() && !input.value) msg = 'Please enter a valid date as MM/DD/YYYY.';
    else if (input.value && typeof input.checkValidity === 'function' && !input.checkValidity()) {
      msg = input.validationMessage || 'This date is outside the allowed range.';
    }
    twin.setCustomValidity(msg);
  }
  // Mask as the user types: digits reflow into MM/DD/YYYY with the / auto-inserted (deletions skip
  // the trailing separator so backspace can't get stuck on it), then sync the native as before.
  twin.addEventListener('input', function(e) {
    var deleting = !!(e && e.inputType && e.inputType.indexOf('delete') === 0);
    var oldVal = twin.value;
    var masked = maskDateInput(oldVal, deleting);
    if (masked !== oldVal) {
      var caret = twin.selectionStart;
      var atEnd = caret == null || caret >= oldVal.length;   // typing at the end — the common case
      twin.value = masked;
      var pos = masked.length;
      if (!atEnd) {   // mid-string edit: re-anchor the caret after the same number of digits
        var digitsBefore = oldVal.slice(0, caret).replace(/\D/g, '').length;
        var seen = 0;
        pos = 0;
        while (pos < masked.length && seen < digitsBefore) {
          if (masked.charAt(pos) >= '0' && masked.charAt(pos) <= '9') seen++;
          pos++;
        }
      }
      if (twin.setSelectionRange) twin.setSelectionRange(pos, pos);
    }
    input.value = mdyToISO(twin.value);
    refreshTwinValidity();
  });
  input.addEventListener('change', function() { twin.value = isoToMDY(input.value); refreshTwinValidity(); });
  twin.value = isoToMDY(input.value);   // reflect whatever the native already holds (default/restored)

  // Open the calendar for a click ANYWHERE on the button. The invisible native covers the whole box,
  // but browsers only open the popup from its internal picker-indicator sub-part (~a dozen px of the
  // shrunken control in Chromium) — the rest is segment-editor dead zone. showPicker() is a spec'd
  // no-op when the picker is already showing, so double-firing with a native indicator click is safe.
  // Throws (no user activation, cross-origin iframe, disabled) fall back to the native indicator,
  // whose hit area date-field.css stretches over the full button in Chromium/WebKit.
  var pickerBox = input.closest('.date-field__picker');
  if (pickerBox && typeof input.showPicker === 'function') {
    pickerBox.addEventListener('click', function() {
      try { input.showPicker(); } catch (e) { /* fall back to native indicator behavior */ }
    });
  }
  input.dataset.dateWired = '1';
  return twin;
}

/** Upgrade + wire every native date input under `root` (default: document). Safe to call repeatedly. */
export function upgradeDateInputs(root) {
  var scope = root || (typeof document !== 'undefined' ? document : null);
  if (!scope) return;
  var inputs = scope.querySelectorAll('input[type="date"]');
  for (var i = 0; i < inputs.length; i++) upgradeDateInput(inputs[i]);
}

/** Reflect a native input's value into its text twin after a PROGRAMMATIC set (no 'change' fires). */
export function syncDateText(nativeId) {
  var native = document.getElementById(nativeId);
  if (!native) return;
  var twin = document.getElementById(nativeId + 'Text');
  if (twin) { twin.value = isoToMDY(native.value); twin.setCustomValidity(''); }  // canonical value → clear stale validity
}

export const DateField = { upgradeDateInput, upgradeDateInputs, syncDateText };
